import { z } from 'zod';
import type { Modality } from '../contracts';
import { customStrategy, customStrategySchema, describeRules, galeText, validateCustomDef, type CustomState, type CustomStrategyDef } from './custom';
import type { Strategy } from './types';

/**
 * Ready-made robots of the platform, picked next to the Automação Invexa. Since 2026-10-26 the admin
 * keeps them (Admin › Estratégias, ApplicationSetting `strategies.presets`): name, game, the "Meus robôs"
 * definition, the gales its numbers are tested with, and the menu order. Each one runs
 * through the same interpreter as "Meus robôs", so a session reads exactly like the backtest.
 *
 * A session copies the definition when it starts (its params), so editing a robot never changes one
 * already running. Sessions started before that carry `{}` and run the built-in definition of their key.
 */

/** Games the ready-made robots play (Rush is off on Polarium). */
export const PRESET_MODALITIES = ['EVEN_ODD', 'RISE_FALL'] as const;
export type PresetModality = (typeof PRESET_MODALITIES)[number];

/**
 * perGame: strategies on one game's list, all run together by the Automação Invexa. Removed ones don't
 * count: they stay stored for good, kept by the server (see savePresets).
 */
export const PRESET_LIMITS = { name: 60, trigger: 200, gales: 10, perGame: 50 } as const;

/**
 * Athena (2026-10-31, removed 2026-11-01): a second automation with its own lists. Her strategies stay
 * stored with `automation: 'athena'`, never shown, never run and never on Thor's list.
 */
const PRESET_AUTOMATIONS = ['invexa', 'athena'] as const;

/** On Thor's list (saved before Athena: no mark). Athena's stored strategies are not. */
export const isThorPreset = (p: Pick<InvexaPreset, 'automation'>): boolean => (p.automation ?? 'invexa') === 'invexa';

/** Keys of the platform's own robots that are not ready-made ones (Athena's too: her past sessions keep it). */
const RESERVED_KEYS: readonly string[] = ['invexa_auto', 'invexa_athena', 'invexa_accu'];
/** At most 39 characters: the Automação Invexa copies the key as a robot id (40 at most). */
const PRESET_KEY_RE = /^invexa_[a-z0-9_]{2,32}$/;

/** A ready-made robot's strategy key (`invexa_…`, never the Automação Invexa or the Rush robot). */
export function isInvexaPresetKey(key: string): boolean {
  return PRESET_KEY_RE.test(key) && !RESERVED_KEYS.includes(key);
}

/** Key of a robot created in the admin: never reused, never renamed. */
export function newPresetKey(): string {
  return `invexa_p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export const invexaPresetSchema = z.object({
  key: z.string().refine(isInvexaPresetKey, 'Identificador inválido'),
  name: z.string().trim().min(2, 'Dê um nome (pelo menos 2 letras).').max(PRESET_LIMITS.name),
  /** The trigger as the user reads it; empty = written from the rules (presetSummary). */
  trigger: z.string().trim().max(PRESET_LIMITS.trigger).default(''),
  modality: z.enum(PRESET_MODALITIES),
  /** Gales the admin's numbers (cycles, assertividade, backtest) are counted with. Never shown to users: they pick their own. */
  gales: z.number().int().min(0).max(PRESET_LIMITS.gales),
  def: customStrategySchema,
  /** Absent = Thor's list. 'athena' = a stored strategy of the removed Athena: kept as it is, never used. */
  automation: z.enum(PRESET_AUTOMATIONS).optional(),
  /** Removed in the admin: off the list, the menu and the Automação Invexa (hidden, never deleted: sessions and history point at it). */
  retired: z.boolean().default(false),
});
export type InvexaPreset = z.infer<typeof invexaPresetSchema>;

/** Errors of a whole list that a single robot can't show. Null = fine. */
export function validatePresetList(list: readonly InvexaPreset[]): string | null {
  const keys = new Set<string>();
  for (const p of list) {
    if (keys.has(p.key)) return `Identificador repetido: ${p.key}.`;
    keys.add(p.key);
    const error = validateCustomDef(p.def, p.modality);
    if (error) return `${p.name}: ${error}`;
  }
  for (const m of PRESET_MODALITIES) {
    const label = `Thor · ${m === 'EVEN_ODD' ? 'Dígitos' : 'Sobe e Desce'}`;
    const names = new Set<string>();
    for (const p of list.filter((x) => x.modality === m && isThorPreset(x) && !x.retired)) {
      const n = p.name.trim().toLocaleLowerCase('pt-BR');
      if (names.has(n)) return `${label}: já existe uma estratégia chamada "${p.name}".`;
      names.add(n);
    }
    // Thor runs the game's whole list: never empty, never more than it can read each round.
    if (!names.size) return `${label}: a lista precisa de pelo menos 1 estratégia (o Thor roda as estratégias da lista).`;
    if (names.size > PRESET_LIMITS.perGame) return `${label}: no máximo ${PRESET_LIMITS.perGame} estratégias na lista.`;
  }
  return null;
}

export const invexaPresetListSchema = z.array(invexaPresetSchema).superRefine((list, ctx) => {
  const error = validatePresetList(list);
  if (error) ctx.addIssue({ code: 'custom', message: error });
});

const def = (d: unknown) => customStrategySchema.parse(d);

/**
 * The robots as they were before the admin (owner's decisions 2026-10-24/25): the most cycles with 0
 * lost cycles at 7 gales on all of Polarium's real rounds we held. Used until the admin saves a list,
 * and for sessions started before 2026-10-26 (their params are `{}`). Past results never promise future ones.
 */
export const DEFAULT_INVEXA_PRESETS: readonly InvexaPreset[] = [
  {
    key: 'invexa_par_impar_par',
    name: 'Par, Ímpar, Par',
    trigger: 'Par, Ímpar, Par → entra Par, depois de 1 loss virtual (gale mantém Par)',
    modality: 'EVEN_ODD',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'PATTERN', sequence: ['EVEN', 'ODD', 'EVEN'], entry: 'EVEN' }], virtualLoss: { count: 1 }, gale: { direction: 'SAME' } }),
    retired: false,
  },
  {
    key: 'invexa_8de10',
    name: '8 de 10',
    trigger: 'Um lado saiu 8+ vezes nas últimas 10 → entra nesse lado, depois de 1 loss virtual (gale troca de lado)',
    modality: 'EVEN_ODD',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'FREQUENCY', window: 10, min: 8, entry: 'MAJORITY' }], virtualLoss: { count: 1 }, gale: { direction: 'INVERT' } }),
    retired: false,
  },
  {
    key: 'invexa_segue6',
    name: 'Segue a sequência',
    trigger: '6 ou mais iguais → entra a favor da sequência',
    modality: 'RISE_FALL',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'STREAK', length: 6, exact: false, entry: 'WITH' }], gale: { direction: 'SIGNAL' } }),
    retired: false,
  },
  {
    key: 'invexa_sdds',
    name: 'Sobe, Desce, Desce, Sobe',
    trigger: 'Sobe, Desce, Desce, Sobe → entra Desce, depois de 1 loss virtual (gale mantém Desce)',
    modality: 'RISE_FALL',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'PATTERN', sequence: ['RISE', 'FALL', 'FALL', 'RISE'], entry: 'FALL' }], virtualLoss: { count: 1 }, gale: { direction: 'SAME' } }),
    retired: false,
  },
  {
    // Second on Dígitos until 26/10, when 8 de 10 passed it (alone and paired with Par, Ímpar, Par).
    key: 'invexa_impar4',
    name: '4 Ímpar → Par',
    trigger: '4 Ímpar seguidos → entra Par (gale mantém Par)',
    modality: 'EVEN_ODD',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'PATTERN', sequence: ['ODD', 'ODD', 'ODD', 'ODD'], entry: 'EVEN' }], gale: { direction: 'SAME' } }),
    retired: true,
  },
  {
    // Took a lost cycle at 7 gales on 25/10 (owner: keep only the best of the history).
    key: 'invexa_quebra3',
    name: 'Quebra de 3',
    trigger: '3 ou mais iguais e virou → entra no lado da virada (gale troca de lado)',
    modality: 'EVEN_ODD',
    gales: 7,
    def: def({ rules: [{ id: 'r1', type: 'BREAK', minRun: 3, entry: 'FOLLOW' }], virtualLoss: { count: 1 }, gale: { direction: 'INVERT' } }),
    retired: true,
  },
];

/**
 * Fast robots (owner 27/10: "entre a todo momento e no gale alterne de acordo com os padrões"): complete
 * tables, every recent history gives a side, so a cycle starts every round and each gale reads the table
 * again (gale SIGNAL). Offered in Admin › Estratégias to put on top of a game's list with one click; at the
 * top it takes every cycle. Picked from 1.300 (Dígitos) / 256 (Sobe e Desce) tables on Polarium's rounds of
 * 17–27/10 (Sobe e Desce from 10/10). Past results never promise future ones.
 */
export const FAST_INVEXA_PRESETS: readonly InvexaPreset[] = [
  {
    // The last number → a side: the best of the tables, 50,4% in all three periods tested.
    key: 'invexa_rapida_digitos',
    name: 'Rápida · último número',
    trigger: 'Toda rodada, pelo último número: 1, 2, 3 ou 9 → Par; 0, 4, 5, 6, 7 ou 8 → Ímpar (gale lê o número de novo)',
    modality: 'EVEN_ODD',
    gales: 7,
    def: def({
      rules: Array.from({ length: 10 }, (_, d) => ({ id: `r${d + 1}`, type: 'PATTERN', sequence: [d], entry: [1, 2, 3, 9].includes(d) ? 'EVEN' : 'ODD' })),
      gale: { direction: 'SIGNAL' },
    }),
    retired: false,
  },
  {
    // The last 3 rounds → a side: the best of the tables, 50,5% in all three periods tested.
    key: 'invexa_rapida_sobe_desce',
    name: 'Rápida · últimas 3',
    trigger: 'Toda rodada, pelas últimas 3: SSS → Sobe, SDD → Sobe, DSD → Sobe; as outras → Desce (gale lê as últimas 3 de novo)',
    modality: 'RISE_FALL',
    gales: 7,
    def: def({
      rules: (['SSS', 'SSD', 'SDS', 'SDD', 'DSS', 'DSD', 'DDS', 'DDD'] as const).map((seq, i) => ({
        id: `r${i + 1}`,
        type: 'PATTERN',
        sequence: [...seq].map((c) => (c === 'S' ? 'RISE' : 'FALL')),
        entry: ['SSS', 'SDD', 'DSD'].includes(seq) ? 'RISE' : 'FALL',
      })),
      gale: { direction: 'SIGNAL' },
    }),
    retired: false,
  },
];

/**
 * Zigue-zague (owner 27/10, the owner's own spec, nothing tuned): the 4 last closed rounds, oldest → newest,
 * ▲ = Par / Sobe, ▼ = Ímpar / Desce. Enters only when the last 3 zigzag, on the side of the 4th one back:
 * ▲▼▲▼ → ▲, ▼▲▼▲ → ▼ (against the last), ▲▲▼▲ → ▲, ▼▼▲▼ → ▼ (with the last); any other 4 waits.
 * Gales against the first entry X: opposite, X, X, X, opposite, opposite, X, X (past the 8th: keep the side).
 * Replaces the whole list of both games from Admin › Estratégias with one click.
 */
export const ZIGZAG_GALE_PLAN = ['OPPOSITE', 'ENTRY', 'ENTRY', 'ENTRY', 'OPPOSITE', 'OPPOSITE', 'ENTRY', 'ENTRY'] as const;

const ZIGZAG_PATTERNS = [
  { n: 1, bars: 'UDUD', entry: 'U', how: 'contra' },
  { n: 2, bars: 'DUDU', entry: 'D', how: 'contra' },
  { n: 3, bars: 'UUDU', entry: 'U', how: 'a favor' },
  { n: 4, bars: 'DDUD', entry: 'D', how: 'a favor' },
] as const;

export const ZIGZAG_INVEXA_PRESETS: readonly InvexaPreset[] = PRESET_MODALITIES.flatMap((modality) => {
  const [up, down] = modality === 'EVEN_ODD' ? (['EVEN', 'ODD'] as const) : (['RISE', 'FALL'] as const);
  const side = (c: 'U' | 'D') => (c === 'U' ? up : down);
  return ZIGZAG_PATTERNS.map((z) => ({
    key: `invexa_zigzag${z.n}_${modality === 'EVEN_ODD' ? 'digitos' : 'sobe_desce'}`,
    name: `Zigue-zague ${z.n} (${z.how})`,
    trigger: '',
    modality,
    gales: ZIGZAG_GALE_PLAN.length,
    def: def({
      rules: [{ id: 'r1', type: 'PATTERN', sequence: [...z.bars].map((c) => side(c as 'U' | 'D')), entry: side(z.entry) }],
      gale: { direction: 'SAME', plan: [...ZIGZAG_GALE_PLAN] },
    }),
    retired: false,
  }));
});

/** The robots of one game on the menu, in the admin's order (Thor's list). */
export function activePresets(list: readonly InvexaPreset[], modality: Modality): InvexaPreset[] {
  return list.filter((p) => p.modality === modality && !p.retired && isThorPreset(p));
}

/**
 * The robots Thor runs on one game: every strategy on its list (owner, 2026-10-26), in list
 * order — when two trigger on the same round, the one above takes the cycle.
 */
export function autoPresets(list: readonly InvexaPreset[], modality: Modality): InvexaPreset[] {
  return activePresets(list, modality).slice(0, PRESET_LIMITS.perGame);
}

/** "sem gale" / "1 gale" / "7 gales". */
export function galesLabel(n: number): string {
  return n <= 0 ? 'sem gale' : n === 1 ? '1 gale' : `${n} gales`;
}

/** The trigger as the user reads it: the admin's text, or one written from the rules. */
export function presetSummary(p: Pick<InvexaPreset, 'trigger' | 'modality' | 'def'>): string {
  if (p.trigger.trim()) return p.trigger.trim();
  const rules = describeRules(p.def.rules, p.modality);
  const vl = p.def.virtualLoss.count;
  return `${rules}${vl ? `, depois de ${vl} ${vl === 1 ? 'loss virtual' : 'losses virtuais'}` : ''} (${galeText(p.def, p.modality)})`;
}

// ---------------------------------------------------------------------------

/** What a session of a ready-made robot runs: a copy taken when it starts. `{}` = sessions before 2026-10-26. */
export const presetParamsSchema = z.union([
  z.object({ v: z.literal(1).default(1), name: z.string().min(1).max(PRESET_LIMITS.name), modality: z.enum(PRESET_MODALITIES), def: customStrategySchema }).strict(),
  z.object({}).strict(),
]);
export type PresetParams = z.infer<typeof presetParamsSchema>;

export function presetParams(p: InvexaPreset): PresetParams {
  return { v: 1, name: p.name, modality: p.modality, def: p.def };
}

/** The definition a session runs: its copy, or the built-in one of its key. */
function presetDefOf(key: string, params: unknown): CustomStrategyDef | null {
  const own = (params as { def?: CustomStrategyDef } | null)?.def;
  return own ?? DEFAULT_INVEXA_PRESETS.find((p) => p.key === key)?.def ?? null;
}

function defOrThrow(key: string, params: unknown): CustomStrategyDef {
  const d = presetDefOf(key, params);
  if (!d) throw new Error(`Unknown preset: ${key}`);
  return d;
}

function presetStrategy(key: string): Strategy<PresetParams, CustomState> {
  const builtIn = DEFAULT_INVEXA_PRESETS.find((p) => p.key === key);
  return {
    key,
    name: builtIn?.name ?? 'Estratégia Invexa',
    description: builtIn ? presetSummary(builtIn) : 'Estratégia pronta da Invexa.',
    modalities: builtIn ? [builtIn.modality] : PRESET_MODALITIES,
    paramsSchema: presetParamsSchema as unknown as z.ZodType<PresetParams>,
    defaultParams: () => ({}),
    validate: (params, modality) => {
      const copy = params as { modality?: Modality; def?: CustomStrategyDef };
      if (copy.def) {
        if (copy.modality !== modality) return 'Estratégia não disponível para esta modalidade.';
        return validateCustomDef(copy.def, modality);
      }
      return builtIn ? null : 'Estratégia indisponível.';
    },
    initialState: (params, modality) => customStrategy.initialState(defOrThrow(key, params), modality),
    onTick: (ctx, tick) => customStrategy.onTick({ ...ctx, params: defOrThrow(key, ctx.params) }, tick),
    onSettlement: (ctx, settled) => customStrategy.onSettlement({ ...ctx, params: defOrThrow(key, ctx.params) }, settled),
    shouldEnd: (state, params) => customStrategy.shouldEnd?.(state, defOrThrow(key, params)) ?? null,
    statusNote: customStrategy.statusNote,
  };
}

/** The strategy of a ready-made robot's key (cheap to build: the runner keeps one per session). Null when the key isn't one. */
export function presetStrategyFor(key: string): Strategy<PresetParams, CustomState> | null {
  return isInvexaPresetKey(key) ? presetStrategy(key) : null;
}
